import { useEffect, useState } from "react";
import { Clock } from "lucide-react";
import { formatAuctionEnd } from "../../lib/auction-utils";

type Props = {
    endsAt: string | null;
    onEnd?: () => void;
    className?: string;
    size?: "sm" | "lg";
};

export function LiveTimer({ endsAt, onEnd, className = "", size = "sm" }: Props) {
    const [timeLeft, setTimeLeft] = useState<number>(() => {
        if (!endsAt) return 0;
        const distance = new Date(endsAt).getTime() - new Date().getTime();
        return Math.max(0, distance);
    });

    useEffect(() => {
        if (!endsAt) return;

        const timer = setInterval(() => {
            const now = new Date().getTime();
            const end = new Date(endsAt).getTime();
            const distance = end - now;

            if (distance <= 0) {
                clearInterval(timer);
                setTimeLeft(0);
                onEnd?.();
            } else {
                setTimeLeft(distance);
            }
        }, 1000);

        return () => clearInterval(timer);
    }, [endsAt, onEnd]);

    if (!endsAt || timeLeft <= 0) {
        return (
            <div className={`flex items-center gap-2 rounded-xl bg-muted px-3 py-2 text-muted-foreground border border-border ${className}`}>
                <Clock className={size === "lg" ? "h-6 w-6" : "h-4 w-4"} />
                <span className={size === "lg" ? "text-sm font-bold" : "text-xs font-bold"}>Avsluttet</span>
            </div>
        );
    }

    const totalSeconds = Math.floor(timeLeft / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    const isCritical = timeLeft < 30000; // Under 30 seconds
    const isShowingCountdown = timeLeft < 3600000; // Under 1 hour

    if (!isShowingCountdown) {
        return (
            <div className={`flex items-center gap-2 rounded-full bg-slate-50/80 px-4 py-1.5 text-slate-900 border border-slate-200 shadow-sm ${className}`}>
                <span className={`${size === "lg" ? "text-lg" : "text-[11px]"} font-bold whitespace-nowrap`}>
                    Avsluttes: {formatAuctionEnd(endsAt)}
                </span>
            </div>
        );
    }

    const timeString = isCritical
        ? "< 30s"
        : hours > 0
            ? `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
            : `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;

    return (
        <div
            className={`flex items-center gap-2 rounded-xl transition-all duration-300 shadow-lg ${size === "lg" ? "px-6 py-4" : "px-4 py-2.5"
                } ${isCritical
                    ? "bg-red-600 border-red-400 animate-[pulse_0.5s_infinite] scale-105 z-10 text-white"
                    : "bg-black/60 backdrop-blur-md border-white/20 text-white"
                } ${className}`}
        >
            <Clock className={`${size === "lg" ? "h-8 w-8" : "h-5 w-5"} ${isCritical ? "text-white" : "text-accent"}`} />
            <div className="flex flex-col leading-none">
                <span className={`${size === "lg" ? "text-xs" : "text-[9px]"} uppercase font-black opacity-70`}>Avslutter:</span>
                <span className={`${size === "lg" ? "text-3xl" : "text-sm"} font-black tracking-tighter font-mono`}>
                    {timeString}
                </span>
            </div>
        </div>
    );
}
